import {useDispatch, useSelector} from 'react-redux';
import {AnyAction} from 'redux';
import {useSaveRunfile} from '../../../../../hooks/useSaveRunfile';
import {IElementQuestionAnswer} from '../../../../../incase-domain/elements/base/questionElementBase';
import {IElementMultiSelect} from '../../../../../incase-domain/elements/question/multiSelect';
import {IGameConfig} from '../../../../../incase-domain/proptypes/gameConfig';
import {QuestionElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {getNewPoints, getNewPointsMultiple} from '../../../../../service/gamification.service';
import {handleElementsClickedAction} from '../../../../../store/actions/chalkBoardElements.actions';
import {gameConfigSelector, loadedPresentationSelector} from '../../../../../store/selectors/presentation.selectors';
import {pointsSelector} from '../../../../../store/selectors/runfile.selectors';
import {isQuestionRetriable, isQuestionRetryLimitReached} from '../../../../../util/util';
import {useQuestionRemainingToast} from '../common/useQuestionRemainingToast';

function useMultiselectTry(): (
    element: IElementMultiSelect,
    elementInteractions: QuestionElementInteractions,
    codebookAnswers: IElementQuestionAnswer[] | undefined
) => void {
    const dispatch = useDispatch();

    const {emitRemainingTriesToast} = useQuestionRemainingToast();
    const saveRunfile = useSaveRunfile();

    const points = useSelector(pointsSelector);
    const gameConfig = useSelector(gameConfigSelector);
    const presentation = useSelector(loadedPresentationSelector);

    function handleTry(
        element: IElementMultiSelect,
        elementInteractions: QuestionElementInteractions,
        codebookAnswers: IElementQuestionAnswer[] | undefined
    ): void {
        const markedAnswers = codebookAnswers || elementInteractions.markedAnswers?.map((c) => element.answers[c]) || [];
        const correctlyChosen = markedAnswers.filter((answer) => answer.type === 'correct');
        const wrongfullyChosen = markedAnswers.filter((answer) => answer.type === 'incorrect');

        let newPoints = getNewPointsMultiple(
            points || 0,
            correctlyChosen.length,
            wrongfullyChosen.length,
            gameConfig || ({} as IGameConfig),
            element.gameConfig
        );

        const totalCorrect = element.answers.filter((answer) => answer.type === 'correct').length;
        const isCorrect = wrongfullyChosen.length === 0 && totalCorrect === correctlyChosen.length;

        if (
            !isQuestionRetriable(element, presentation) ||
            isQuestionRetryLimitReached(elementInteractions, element, presentation, elementInteractions.answered, isCorrect)
        ) {
            saveRunfile(
                {
                    ...elementInteractions,
                    clickedAnswersIds: elementInteractions.markedAnswersIds || [],
                    clickedAnswers: elementInteractions.markedAnswers,
                    answered: true,
                },
                element.id,
                element.slideId,
                newPoints
            );
        } else {
            newPoints = getNewPoints(newPoints, isCorrect, gameConfig || ({} as IGameConfig), element.gameConfig);
            emitRemainingTriesToast(element, elementInteractions);

            saveRunfile(
                {
                    retriedAnswers: [...(elementInteractions.retriedAnswers || []), {attempt: elementInteractions.markedAnswers}],
                },
                element.id,
                element.slideId,
                newPoints
            );
        }
        const handleElementsClickedActionCreator = (): AnyAction => handleElementsClickedAction([element.id], !!element.clearAfterClicked);
        dispatch(handleElementsClickedActionCreator());
    }

    return handleTry;
}

export default useMultiselectTry;
